/**
 * @file bodycamMappingsApi.ts
 *
 * Data layer for bodycam user mappings — the links between a KARDA user and
 * the vendor-side officer id that bodycam evidence is attributed to.
 *
 * Every route here is station-scoped and needs `users:admin`; the token's
 * org_id must equal the stationId passed in (karda-admins bypass that check),
 * so a station admin can only ever address their own station.
 */
import { profileClient } from "@/shared/api/clients";
import { extractApiError } from "@/shared/api/apiError";
import type {
  ExternalUserMapping,
  ListMappingsResponse,
  ListVendorUsersResponse,
  ResyncResult,
  ResyncScope,
} from "@/shared/interfaces/ExternalUserMapping";
import {
  BODYCAM_NOT_CONFIGURED,
  BODYCAM_VENDOR_UNSUPPORTED,
} from "@/shared/interfaces/ExternalUserMapping";

const INTEGRATION = "bodycam";

function mappingsPath(stationId: string): string {
  return `/v1/stations/${stationId}/integrations/${INTEGRATION}/mappings`;
}

/**
 * A station's vendor roster, or the settled reason there isn't one.
 * "unavailable" covers only the answers that won't change on a retry (no
 * binding, a vendor with no directory loader) — transient vendor failures
 * throw so react-query and the retry button can handle them.
 */
export type VendorDirectoryEntry =
  | { status: "ready"; data: ListVendorUsersResponse }
  | { status: "unavailable"; code: string; message: string };

/** Codes that mean "this station has no roster to show", not "try again". */
const SETTLED_DIRECTORY_CODES = new Set([
  BODYCAM_NOT_CONFIGURED,
  BODYCAM_VENDOR_UNSUPPORTED,
  "vendor_directory_unsupported",
]);

/**
 * The whole partition comes back in one query — no pagination — sorted
 * ascending by externalUserId.
 */
export async function listMappings(
  stationId: string,
  includeInactive = false,
): Promise<ExternalUserMapping[]> {
  const resp = await profileClient.get<ListMappingsResponse>(
    `${mappingsPath(stationId)}?includeInactive=${includeInactive}`,
  );
  return resp.data.mappings ?? [];
}

/**
 * Slow by design: the vendor scan runs under a 10s budget inside a 30s Lambda
 * and returns the entire roster at once, so this is cached per station and
 * must never be driven from a search field.
 */
export async function listVendorDirectory(
  stationId: string,
): Promise<VendorDirectoryEntry> {
  try {
    const resp = await profileClient.get<ListVendorUsersResponse>(
      `/v1/stations/${stationId}/integrations/${INTEGRATION}/vendor-users`,
    );
    return { status: "ready", data: resp.data };
  } catch (err) {
    const { code, message } = extractApiError(err);
    if (code && SETTLED_DIRECTORY_CODES.has(code)) {
      return {
        status: "unavailable",
        code,
        message: message ?? "No vendor directory is available.",
      };
    }
    throw err;
  }
}

export async function createMapping(
  stationId: string,
  payload: { externalUserId: string; userId: string; active?: boolean },
): Promise<ExternalUserMapping> {
  const resp = await profileClient.post<{ mapping: ExternalUserMapping }>(
    mappingsPath(stationId),
    { active: true, ...payload },
  );
  return resp.data.mapping;
}

/** Re-points an existing vendor id; POST would 409 on the existing row. */
export async function updateMapping(
  stationId: string,
  externalUserId: string,
  payload: { userId?: string; active?: boolean },
): Promise<ExternalUserMapping> {
  const resp = await profileClient.put<{ mapping: ExternalUserMapping }>(
    `${mappingsPath(stationId)}/${encodeURIComponent(externalUserId)}`,
    payload,
  );
  return resp.data.mapping;
}

export async function deleteMapping(
  stationId: string,
  externalUserId: string,
): Promise<void> {
  await profileClient.delete(
    `${mappingsPath(stationId)}/${encodeURIComponent(externalUserId)}`,
  );
}

export async function triggerResync(
  stationId: string,
  scope: ResyncScope = "unmapped_only",
): Promise<ResyncResult> {
  const resp = await profileClient.post<ResyncResult>(
    `/v1/stations/${stationId}/integrations/${INTEGRATION}/resync`,
    { scope },
  );
  return resp.data;
}
